import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, Filter, Mail, Phone, Building, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Acheteur', 'Vendeur', 'Aménageur', 'Promoteur', 'Investisseur'];

const getCategoryColor = (categorie) => {
  const colors = {
    'Acheteur': 'bg-blue-100 text-blue-800',
    'Vendeur': 'bg-green-100 text-green-800',
    'Aménageur': 'bg-purple-100 text-purple-800',
    'Promoteur': 'bg-orange-100 text-orange-800',
    'Investisseur': 'bg-yellow-100 text-yellow-800'
  };
  return colors[categorie] || 'bg-gray-100 text-gray-800';
};

const Contacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        withCredentials: true
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.prenoms.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      contact.telephone.includes(searchTerm);
    
    const matchesCategory = filterCategorie === 'all' || contact.categorie === filterCategorie;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002255]"></div>
      </div>
    );
  }

  return (
    <div data-testid="contacts-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#002255] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contacts
          </h1>
          <p className="text-gray-600">Gérez vos contacts et prospects</p>
        </div>
        <Button
          onClick={() => navigate('/contacts/new')}
          className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white"
          data-testid="add-contact-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Contact
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/40 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Rechercher un contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-contact-input"
            />
          </div>
          <Select value={filterCategorie} onValueChange={setFilterCategorie}>
            <SelectTrigger data-testid="filter-category-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/40 overflow-hidden">
        {filteredContacts.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Aucun contact trouvé</p>
            <Button
              onClick={() => navigate('/contacts/new')}
              className="bg-[#002255] hover:bg-[#002255]/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer votre premier contact
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom Complet</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContacts.map(contact => (
                <TableRow
                  key={contact.contact_id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/contacts/${contact.contact_id}`)}
                  data-testid={`contact-row-${contact.contact_id}`}
                >
                  <TableCell>
                    <div className="font-medium text-[#002255]">
                      {contact.nom} {contact.prenoms}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getCategoryColor(contact.categorie)}>
                      {contact.categorie}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {contact.telephone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span>{contact.telephone}</span>
                        </div>
                      )}
                      {contact.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span>{contact.email}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {contact.entreprise ? (
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span>{contact.entreprise}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{contact.source || '-'}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default Contacts;
